import {Component, inject, OnInit, signal, WritableSignal} from '@angular/core';
import {CompanyRouteContextService} from '@services/company-route-context.service';
import {CompanyService} from '@services/company.service';
import {ICompanyKbis, ICompanyUserFile} from '@models/workspace.interface';
import {DatePipe} from '@angular/common';
import {NotificationService} from '@services/frontend/notification.service';
import {httpErrorMessage} from 'app/utils/http-error.utils';

@Component({
  selector: 'app-company-kbis',
  imports: [DatePipe],
  templateUrl: './company-kbis.html'
})
export class CompanyKbis implements OnInit {
  private readonly companyService = inject(CompanyService);
  private readonly routeContext = inject(CompanyRouteContextService);
  private readonly notificationService = inject(NotificationService);

  loading: WritableSignal<boolean> = signal(false);
  uploading: WritableSignal<boolean> = signal(false);
  kbis: WritableSignal<ICompanyKbis | null> = signal(null);
  manualFiles: WritableSignal<ICompanyUserFile[]> = signal([]);
  private siret: string | null = null;

  ngOnInit(): void {
    this.siret = this.routeContext.currentSiret();
    this.loadKbis();
    this.loadManualKbis();
  }

  loadKbis(): void {
    const siret = this.siret;
    if (!siret) return;
    this.loading.set(true);
    this.companyService.getKbis(siret).subscribe({
      next: (value) => {
        this.kbis.set(value);
        this.loading.set(false);
      },
      error: () => {
        this.loading.set(false);
      }
    });
  }

  loadManualKbis(): void {
    if (!this.siret) return;
    this.companyService.listUserFiles(this.siret, 'KBIS').subscribe({
      next: (files) => this.manualFiles.set(files),
      error: () => this.manualFiles.set([]),
    });
  }

  downloadKbis(): void {
    const current = this.kbis();
    if (!current?.id) return;
    this.companyService.downloadDocument(current.id).subscribe({
      next: (blob) => {
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'kbis.pdf';
        a.click();
        window.URL.revokeObjectURL(url);
      },
      error: () => this.notificationService.error('Impossible de telecharger le KBIS', 2500),
    });
  }

  onFileSelected(event: Event): void {
    if (!this.siret) return;
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    input.value = '';
    if (!file) {
      return;
    }
    this.uploading.set(true);
    this.companyService.uploadUserFile(this.siret, file, 'KBIS').subscribe({
      next: () => {
        this.uploading.set(false);
        this.notificationService.success('KBIS uploadé avec succès', 2500);
        this.loadManualKbis();
      },
      error: (err) => {
        this.uploading.set(false);
        this.notificationService.error(httpErrorMessage(err, 'Échec de l’envoi du KBIS'), 5000);
      },
    });
  }

  downloadManual(fileId: string, originalFilename: string): void {
    if (!this.siret) return;
    this.companyService.downloadUserFile(this.siret, fileId).subscribe({
      next: (blob) => {
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = originalFilename;
        a.click();
        window.URL.revokeObjectURL(url);
      },
      error: () => this.notificationService.error('Téléchargement impossible', 3000),
    });
  }
}
